import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * RewardsCreditBlock — Earn Rewrite (2026-08-13).
 *
 * Read-only 3-line reveal on the passport page below the tier bar.
 * Copy is locked by Amendment C:
 *   Line 1 (prominent):  "N Reward Points · $Y.YY TravelBio Credit"
 *   Line 2 (muted):      "M Lifetime Points · Counts toward your TravelBio tier"
 *   Line 3 (italic hand):"Use your TravelBio Credit toward eligible TravelBio bookings."
 *
 * NO Apply / Redeem CTA.  USD comes from the server-computed
 * `redeemable_credit_usd` field — the frontend never hard-codes the
 * conversion divisor.  User-facing wording is always "Reward Points"
 * / "Lifetime Points" — never "Available" or "Redeemable Points".
 */
export default function RewardsCreditBlock() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/rewards/v2/tier-status");
        if (!ignore) setStatus(data);
      } catch {
        // Silent — tier bar handles the outage banner separately.
      }
    })();
    return () => { ignore = true; };
  }, []);

  if (!status) return null;

  const rewardPts = Number(status.redeemable_points || 0);
  const credit = Number(status.redeemable_credit_usd || 0);
  const lifetime = Number(status.lifetime_points || 0);
  const creditFmt = credit.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div
      data-testid="rewards-credit-block"
      className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 space-y-1"
    >
      {/* Line 1 — Reward Points (prominent, main serif italic). */}
      <div
        data-testid="rewards-credit-line1"
        className="font-serif-it text-tb-navy text-lg leading-tight"
      >
        <span data-testid="rewards-credit-points">
          {rewardPts.toLocaleString()} Reward Points
        </span>
        <span className="text-tb-navy/40 px-1.5">·</span>
        <span data-testid="rewards-credit-usd">
          ${creditFmt} TravelBio Credit
        </span>
      </div>

      {/* Line 2 — Lifetime Points (muted subtitle). */}
      <div
        data-testid="rewards-credit-line2"
        className="font-hand text-tb-navy/60 text-sm leading-tight"
      >
        <span data-testid="rewards-credit-lifetime">
          {lifetime.toLocaleString()} Lifetime Points
        </span>
        <span className="text-tb-navy/30 px-1.5">·</span>
        <span>Counts toward your TravelBio tier</span>
      </div>

      {/* Line 3 — helper (existing italic hand style). */}
      <p
        data-testid="rewards-credit-helper"
        className="font-hand text-[11px] italic text-tb-navy/55 leading-snug"
      >
        Use your TravelBio Credit toward eligible TravelBio bookings.
      </p>
    </div>
  );
}
